/**
 * Pure helpers over a Check setup report (Rust run_doctor `Item`s).
 *
 * status: ok | error (blocks Start) | warn | suggest | blocked (waits for a service)
 */

export const ATTENTION = new Set(['error', 'warn']);

/** Items that need attention (✗ and !), ports of live runs left out. */
export function attentionItems(report, { liveNames = new Set() } = {}) {
  return (report?.items || []).filter((i) => ATTENTION.has(i.status) && !(i.kind === 'port' && i.configs.some((n) => liveNames.has(n))));
}

/** Errors that concern any of `names` (the configurations about to start). Ports are left to startRun. */
export function blockingFor(report, names) {
  const want = new Set(names);
  return (report?.items || []).filter((i) => i.status === 'error' && i.kind !== 'port' && i.configs.some((n) => want.has(n)));
}

/**
 * "Fix all safe items": project-local steps only (submodules → deps → .env
 * copy), in that order. Never toolchains, services, docker or migrations.
 */
export function safeSteps(report) {
  return (report?.items || [])
    .filter((i) => i.status === 'error' && i.fix?.safe && [1, 2, 3].includes(i.fix.step))
    .sort((a, b) => a.fix.step - b.fix.step);
}

/** A command as one line (`npm ci`). */
export function commandText(cmd) {
  const quote = (a) => (/[\s"]/.test(a) || a === '' ? JSON.stringify(a) : a);
  return [cmd.program, ...(cmd.args || [])].map(quote).join(' ');
}

/** What a fix does, as the confirm dialog and the checklist show it. */
export function fixLines(item) {
  const fix = item.fix;
  if (!fix) return [];
  if (fix.action === 'env_copy') return [`cp ${fix.from} ${fix.file}`];
  return (fix.commands || []).map(commandText);
}

/**
 * The pseudo-configuration a setup command is approved as: the same SHA-256
 * first-run approval as launch.json commands (runApproval.js), keyed by item.
 */
export function fixConfig(item, cmd, index) {
  return { name: `setup:${item.id}:${index}`, runtimeExecutable: cmd.program, runtimeArgs: cmd.args || [], cwd: item.fix.cwd || '' };
}

export function currentOs(nav = typeof navigator !== 'undefined' ? navigator : null) {
  const ua = `${nav?.userAgentData?.platform || ''} ${nav?.platform || ''} ${nav?.userAgent || ''}`;
  if (/mac/i.test(ua)) return 'macos';
  if (/win/i.test(ua)) return 'windows';
  return 'linux';
}

/** The install hints for this computer (its OS + the OS-independent ones). */
export function hintsFor(item, os = currentOs()) {
  return (item.hints || []).filter((h) => h.os === os || h.os === 'any');
}

const ORDER = { error: 0, warn: 1, blocked: 2, suggest: 3, ok: 4 };
const KIND_ORDER = { submodules: 0, cwd: 0, toolchain: 1, deps: 2, env: 3, service: 4, docker: 5, db_setup: 6, port: 7 };

/** Checklist order: problems first, then by kind (the order fixes are applied). */
export function sortItems(items) {
  return [...(items || [])].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9) || (KIND_ORDER[a.kind] ?? 9) - (KIND_ORDER[b.kind] ?? 9));
}
